"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { Meter, toneFor } from "@/components/tools/admissions/meter";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  analyseUcas,
  exportUcas,
  UCAS_BUDGETS,
  UCAS_LIMIT,
  UCAS_MINIMUM,
  UCAS_PRESETS,
  UCAS_QUESTIONS,
  type UcasAnswers,
  type UcasPreset,
  type UcasQuestion,
} from "@/lib/tool-kit/admissions/ucas";
import { downloadText } from "@/lib/tool-kit/download";

/** English strings. A translated content file overrides these through `ui`. */
export const UCAS_PERSONAL_STATEMENT_PLANNER_STRINGS = {
  setupHeading: "Plan your three answers",
  budgetLabel: "Starting budget for the 4,000 characters",
  budgetHint:
    "These splits are only starting points — UCAS lets you divide the 4,000 characters however you like.",
  budgetOptions: {
    even: "Even (1,400 / 1,300 / 1,300)",
    subject: "Subject-led (1,800 / 1,200 / 1,000)",
    experience: "Experience-led (1,200 / 1,100 / 1,700)",
  },
  breakLabel: "Count each line break as",
  breakHint:
    "UCAS does not say how it counts a line break. One character is the usual reading; choose two to see the worst case, then check the counter in your UCAS Hub.",
  breakOptions: { "1": "1 character", "2": "2 characters" },
  totalLabel: "Characters used",
  remainingLabel: "Characters left",
  overLabel: "Characters over the limit",
  limitNote:
    "Limit: {limit} characters including spaces, shared by all three answers.",
  questionHeading: "Question {n}",
  questions: {
    q1: "Why do you want to study this course or subject?",
    q2: "How have your qualifications and studies helped you to prepare for this course or subject?",
    q3: "What else have you done to prepare outside of education, and why are these experiences useful?",
  },
  guidanceHeading: "What UCAS suggests covering",
  guidance: {
    q1: [
      "Your motivation: what drew you to the subject, and why it suits you.",
      "Your own reading, research and super-curricular activity — not only what the syllabus set.",
      "Your plans for the future, if they connect to the course.",
    ],
    q2: [
      "How your formal education and the skills it built prepare you for this course.",
      "Recent and relevant work first, rather than a tour of everything.",
      "Not your grades: universities see them elsewhere on your application. Competitions and leadership roles are better evidence.",
    ],
    q3: [
      "Work experience, volunteering, hobbies, personal experiences or achievements.",
      "For each, why it is useful to this course — the link matters more than the list.",
      "If you are no longer in education, what you have done since.",
    ],
  },
  notesLabel: "Your notes for question {n} (not part of the statement)",
  notesPlaceholder:
    "Bullet points: examples, books, dates, what each taught you…",
  draftLabel: "Your answer to question {n}",
  draftPlaceholder: "Write or paste your answer here.",
  planTarget: "Plan target: {target}",
  characterCount: "one:{count} character|other:{count} characters",
  meterState: {
    empty: "Not started — {min} needed.",
    short: "{shortfall} more needed to reach the minimum of {min}.",
    ok: "Minimum of {min} reached.",
  },
  versusPlan: {
    under: "{gap} under your plan target.",
    over: "{gap} over your plan target.",
    on: "Exactly on your plan target.",
  },
  copyAnswer: "Copy answer {n}",
  copied: "Copied!",
  resultsHeading: "Whole statement",
  statusReady:
    "Within the limit, and every answer has the minimum of {min} characters.",
  statusOver: "Over the limit by {gap}. Cut before you paste.",
  statusShort:
    "Within the limit, but at least one answer is under the minimum of {min} characters.",
  statusEmpty: "Start writing to see the count.",
  download: "Download answers and notes (.txt)",
  downloadName: "ucas-personal-statement.txt",
  clear: "Clear everything",
  exportNotes: "Notes",
  exportAnswer: "Answer",
  exportBudget: "Plan target",
  exportCharacters: "{count} characters",
  privacy:
    "Everything stays in your browser. Nothing is uploaded or saved, so copy your work out before you close the page.",
} as const;

type Strings = typeof UCAS_PERSONAL_STATEMENT_PLANNER_STRINGS;

const EMPTY: UcasAnswers = { q1: "", q2: "", q3: "" };

function QuestionCard({
  question,
  index,
  strings,
  draft,
  notes,
  characters,
  budget,
  onDraft,
  onNotes,
}: {
  question: UcasQuestion;
  index: number;
  strings: Strings;
  draft: string;
  notes: string;
  characters: number;
  budget: number;
  onDraft: (value: string) => void;
  onNotes: (value: string) => void;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const n = String(index + 1);
  const shortfall = Math.max(0, UCAS_MINIMUM - characters);
  let stateKey: keyof Strings["meterState"] = "ok";
  if (characters === 0) {
    stateKey = "empty";
  } else if (shortfall > 0) {
    stateKey = "short";
  }
  const gap = budget - characters;
  let planKey: keyof Strings["versusPlan"] = "on";
  if (characters > 0 && gap > 0) {
    planKey = "under";
  } else if (gap < 0) {
    planKey = "over";
  }
  const counted = (count: number) =>
    tPlural(strings.characterCount, locale, count, { count: fmt(count) });
  const caption = [
    counted(characters),
    t(strings.meterState[stateKey], {
      min: fmt(UCAS_MINIMUM),
      shortfall: fmt(shortfall),
    }),
    characters === 0
      ? ""
      : t(strings.versusPlan[planKey], { gap: counted(Math.abs(gap)) }),
  ]
    .filter((part) => part !== "")
    .join(" ");
  return (
    <article className={`${CARD} space-y-4`}>
      <div className="space-y-1">
        <h3 className={WIDGET_H3}>{t(strings.questionHeading, { n })}</h3>
        <p className="font-medium text-foreground text-sm">
          {strings.questions[question]}
        </p>
        <p className={HINT}>
          {t(strings.planTarget, { target: counted(budget) })}
        </p>
      </div>
      <details className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
        <summary className="cursor-pointer font-medium text-foreground">
          {strings.guidanceHeading}
        </summary>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
          {strings.guidance[question].map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </details>
      <TextAreaField
        id={`ucas-notes-${n}`}
        label={t(strings.notesLabel, { n })}
        onChange={onNotes}
        placeholder={strings.notesPlaceholder}
        prose
        rows={3}
        value={notes}
      />
      <TextAreaField
        id={`ucas-draft-${n}`}
        label={t(strings.draftLabel, { n })}
        onChange={onDraft}
        placeholder={strings.draftPlaceholder}
        prose
        rows={10}
        value={draft}
      />
      <Meter
        caption={caption}
        limit={Math.max(budget, characters, UCAS_MINIMUM)}
        marker={UCAS_MINIMUM}
        tone={toneFor(characters, budget)}
        value={characters}
      />
      <CopyButton
        copiedLabel={strings.copied}
        disabled={draft.trim() === ""}
        label={t(strings.copyAnswer, { n })}
        text={draft.trim()}
      />
    </article>
  );
}

export default function UcasPersonalStatementPlannerWidget() {
  const strings = useToolUi(UCAS_PERSONAL_STATEMENT_PLANNER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [answers, setAnswers] = useState<UcasAnswers>(EMPTY);
  const [notes, setNotes] = useState<UcasAnswers>(EMPTY);
  const [preset, setPreset] = useState<UcasPreset>("even");
  const [breakCost, setBreakCost] = useState<"1" | "2">("1");
  const cost = breakCost === "2" ? 2 : 1;
  const analysis = useMemo(() => analyseUcas(answers, cost), [answers, cost]);
  const budgets = UCAS_BUDGETS[preset];
  const gapText = tPlural(
    strings.characterCount,
    locale,
    Math.abs(analysis.remaining),
    {
      count: fmt(Math.abs(analysis.remaining)),
    }
  );

  let status: string = strings.statusEmpty;
  if (analysis.over) {
    status = t(strings.statusOver, { gap: gapText });
  } else if (analysis.ready) {
    status = t(strings.statusReady, { min: fmt(UCAS_MINIMUM) });
  } else if (analysis.total > 0) {
    status = t(strings.statusShort, { min: fmt(UCAS_MINIMUM) });
  }

  function download() {
    const text = exportUcas(
      answers,
      notes,
      budgets,
      {
        questions: strings.questions,
        notes: strings.exportNotes,
        answer: strings.exportAnswer,
        budget: strings.exportBudget,
        characters: (count) =>
          t(strings.exportCharacters, { count: fmt(count) }),
      },
      cost
    );
    downloadText(strings.downloadName, text);
  }

  return (
    <div className="space-y-8">
      <section className={`${CARD} space-y-5`}>
        <h2 className={WIDGET_H2}>{strings.setupHeading}</h2>
        <PillGroup
          label={strings.budgetLabel}
          name="ucas-budget"
          onChange={setPreset}
          options={UCAS_PRESETS.map((value) => ({
            value,
            label: strings.budgetOptions[value],
          }))}
          value={preset}
        />
        <p className={HINT}>{strings.budgetHint}</p>
        <PillGroup
          label={strings.breakLabel}
          name="ucas-breaks"
          onChange={setBreakCost}
          options={[
            { value: "1", label: strings.breakOptions["1"] },
            { value: "2", label: strings.breakOptions["2"] },
          ]}
          value={breakCost}
        />
        <p className={HINT}>{strings.breakHint}</p>
      </section>

      <div className="space-y-6">
        {UCAS_QUESTIONS.map((question, index) => (
          <QuestionCard
            budget={budgets[question]}
            characters={analysis.answers[index].characters}
            draft={answers[question]}
            index={index}
            key={question}
            notes={notes[question]}
            onDraft={(value) =>
              setAnswers((current) => ({ ...current, [question]: value }))
            }
            onNotes={(value) =>
              setNotes((current) => ({ ...current, [question]: value }))
            }
            question={question}
            strings={strings}
          />
        ))}
      </div>

      <section className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        <StatGrid>
          <StatCard
            emphasis
            label={strings.totalLabel}
            value={`${fmt(analysis.total)} / ${fmt(UCAS_LIMIT)}`}
          />
          <StatCard
            label={analysis.over ? strings.overLabel : strings.remainingLabel}
            value={fmt(Math.abs(analysis.remaining))}
          />
        </StatGrid>
        <Meter
          caption={status}
          limit={UCAS_LIMIT}
          tone={toneFor(analysis.total, UCAS_LIMIT)}
          value={analysis.total}
        />
        <p className={HINT}>
          {t(strings.limitNote, { limit: fmt(UCAS_LIMIT) })}
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            disabled={analysis.total === 0}
            onClick={download}
            type="button"
          >
            {strings.download}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              setAnswers(EMPTY);
              setNotes(EMPTY);
            }}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
        <p className={HINT}>{strings.privacy}</p>
      </section>
    </div>
  );
}
